import { createClient } from '@supabase/supabase-js';

// Universal env reader.
//
// CRITICAL: In Vite browser builds, only `import.meta.env.VITE_*` references
// are statically replaced at build time. `process.env` is NOT available in
// the browser, and `window.__VITE_ENV` is not populated by Vite. Reading
// only from those sources caused the production frontend to throw
// "Missing required environment variable: SUPABASE_URL" even when Vercel
// had VITE_SUPABASE_URL configured.
//
// We read literal `import.meta.env.VITE_*` keys (so Vite can inline them),
// then fall back to `process.env` for Node/SSR/test environments.
const getEnvVar = (key: 'SUPABASE_URL' | 'SUPABASE_ANON_KEY'): string | undefined => {
  // Browser / Vite: literal references so the bundler can inline values.
  let viteVal: string | undefined;
  try {
    const meta = (import.meta as any)?.env ?? {};
    if (key === 'SUPABASE_URL') {
      viteVal = meta.VITE_SUPABASE_URL || meta.SUPABASE_URL;
    } else {
      viteVal =
        meta.VITE_SUPABASE_ANON_KEY ||
        meta.VITE_SUPABASE_PUBLISHABLE_KEY ||
        meta.SUPABASE_ANON_KEY ||
        meta.SUPABASE_PUBLISHABLE_KEY;
    }
  } catch {
    // import.meta unavailable (CJS/Node) — fall through to process.env
  }
  if (viteVal) return viteVal;

  // Node / SSR / test fallback.
  if (typeof process !== 'undefined' && process.env) {
    const aliases =
      key === 'SUPABASE_URL'
        ? ['SUPABASE_URL', 'VITE_SUPABASE_URL']
        : [
            'SUPABASE_ANON_KEY',
            'VITE_SUPABASE_ANON_KEY',
            'SUPABASE_PUBLISHABLE_KEY',
            'VITE_SUPABASE_PUBLISHABLE_KEY',
          ];
    for (const a of aliases) {
      if (process.env[a]) return process.env[a];
    }
  }

  return undefined;
};


// Env-required: NEVER fall back to a production project ref. Missing env must
// crash the build/runtime so staging cannot silently call production.
const supabaseUrl = getEnvVar('SUPABASE_URL');
const supabaseAnonKey = getEnvVar('SUPABASE_ANON_KEY');
if (!supabaseUrl) {
  throw new Error(
    '[supabase] Missing required environment variable: SUPABASE_URL ' +
    '(or VITE_SUPABASE_URL). Configure it in Vercel / Lovable Cloud / Railway ' +
    'before building. Refusing to fall back to a production URL.'
  );
}
if (!supabaseAnonKey) {
  throw new Error(
    '[supabase] Missing required environment variable: SUPABASE_ANON_KEY ' +
    '(or VITE_SUPABASE_ANON_KEY / VITE_SUPABASE_PUBLISHABLE_KEY). Configure ' +
    'it in Vercel / Lovable Cloud / Railway before building.'
  );
}

/**
 * Shared Supabase client for the monorepo.
 * 
 * CRITICAL: Uses consistent storageKey across all apps to prevent Chrome
 * from treating the Supabase domain as an "intermediate website" during OAuth.
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Consistent storage key across all apps - prevents Chrome intermediate site issues
    storageKey: 'bright-web-supabase-auth',
    // Use localStorage for session persistence (browser-safe check)
    storage: typeof globalThis !== 'undefined' && typeof (globalThis as any).localStorage !== 'undefined' 
      ? (globalThis as any).localStorage 
      : undefined,
    // Persist session across page reloads and browser restarts
    persistSession: true,
    // Automatically refresh token when it's about to expire
    autoRefreshToken: true,
    // Detect session from URL (for OAuth redirects)
    detectSessionInUrl: true,
    // Flow type for OAuth
    flowType: 'pkce',
  },
});

// DEV-only: expose supabase client for debugging RPC calls in DevTools
// Gated behind explicit opt-in: ?debugBw=1 in URL (dev mode only)
if (typeof globalThis !== 'undefined' && typeof (globalThis as any).window !== 'undefined') {
  try {
    const win = (globalThis as any).window;
    const isDev = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') ||
                  win.__VITE_DEV__;
    if (isDev && win.location?.search?.includes('debugBw=1')) {
      win.__bw = win.__bw ?? {};
      win.__bw.supabase = supabase;
    }
  } catch {
    // Silently fail in restricted environments
  }
}
